import type { AsyncActionButtonProps } from '@blueshift-ui/inputs/dist/types/async-action-button-props';
import type { ButtonProps } from '@mui/material/Button/Button';
import type { DialogProps } from '@blueshift-ui/feedback';
import type { ReactElement } from 'react';

import * as sharedStyles from '../index.css';
import { TrackedButton, TrackedDialog } from '@blueshift-ui/analytics';
import AsyncActionButton from '@blueshift-ui/inputs/dist/components/async-action-button';
import Typography from '@blueshift-ui/theme/dist/components/typography';
import useBreakpoints from '@blueshift-ui/theme/dist/hooks/use-breakpoints';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

interface ConfirmationDialogProps extends DialogProps {
  asyncButtonProps?: Partial<AsyncActionButtonProps>;
  cancelButtonProps?: ButtonProps;
  cancelText?: ReactElement;
  confirmText?: ReactElement;
  onAsyncConfirm?: () => Promise<void>;
  onCancel?: () => void;
  onConfirm?: () => void;
}

function ConfirmationDialog({
  asyncButtonProps,
  cancelButtonProps,
  cancelText,
  confirmText,
  children,
  onAsyncConfirm,
  onCancel,
  onConfirm,
  ...dialogProps
}: ConfirmationDialogProps) {
  const { isSmallViewport } = useBreakpoints();
  const { loading, translate } = useTranslation('confirmation_dialog');

  if (loading) {
    return null;
  }

  return (
    <TrackedDialog
      actions={
        <div className={sharedStyles.dialogActions}>
          {onAsyncConfirm ? (
            <AsyncActionButton
              action={onAsyncConfirm}
              className={sharedStyles.fullWidthOnMobile}
              component={TrackedButton}
              id="confirm_button"
              variant="contained"
              {...asyncButtonProps}
            >
              {confirmText || translate('confirm')}
            </AsyncActionButton>
          ) : (
            <TrackedButton
              className={sharedStyles.fullWidthOnMobile}
              id="confirm_button"
              onClick={onConfirm}
              variant="contained"
            >
              {confirmText || translate('confirm')}
            </TrackedButton>
          )}
          <TrackedButton
            className={sharedStyles.fullWidthOnMobile}
            id="cancel_button"
            onClick={onCancel}
            variant="outlined"
            {...cancelButtonProps}
          >
            {cancelText || translate('cancel')}
          </TrackedButton>
        </div>
      }
      className={sharedStyles.dialog}
      fullScreen={isSmallViewport}
      id="confirmation_dialog"
      maxWidth="md"
      withCloseButton={true}
      {...dialogProps}
    >
      <Typography color="var(--ref-palette-secondary-shade20)" variant="bodyMedium">
        {children}
      </Typography>
    </TrackedDialog>
  );
}

export type { ConfirmationDialogProps };

export default ConfirmationDialog;
